import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { axios } from "@/admin/libs/axios";

export type CampaignOfferType = "FREE_ACCESS" | "STRIPE_DISCOUNT";
export type CampaignAudience = "EVERYONE" | "NEW_USERS" | "FREE_USERS";
export type PlanType = "free" | "starter_pass" | "pro" | "lifetime";

export interface AdminCampaignRow {
  id: string;
  slug: string;
  name: string;
  offerType: CampaignOfferType;
  enabled: boolean;
  startsAt: string;
  endsAt: string;
  targetAudience: CampaignAudience;
  grantedPlan: PlanType;
  grantedDurationDays: number;
  discountPercent: number | null;
  discountDurationMonths: number | null;
  stripeCouponId: string | null;
  maxRedemptions: number | null;
  currentRedemptions: number;
  // Computed metrics for the list view.
  metrics: {
    views: number;
    claims: number;
    conversions: number;
    grossRevenueCents: number;
  };
  status: "active" | "scheduled" | "ended" | "paused";
}

export interface AdminCampaignDetail extends AdminCampaignRow {
  description: string;
  bannerText: string | null;
  pageSlug: string | null;
  pageHeadline: string | null;
  pageSubheadline: string | null;
  targetCountries: string[];
  targetPlans: PlanType[];
  discountPlans: PlanType[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCampaignInput {
  slug: string;
  name: string;
  description: string;
  offerType: CampaignOfferType;
  grantedPlan: PlanType;
  grantedDurationDays: number;
  startsAt: string;
  endsAt: string;
  targetAudience: CampaignAudience;
  targetCountries?: string[];
  targetPlans?: PlanType[];
  maxRedemptions?: number | null;
  bannerText?: string | null;
  pageSlug?: string | null;
  pageHeadline?: string | null;
  pageSubheadline?: string | null;
  discountPercent?: number | null;
  discountPlans?: PlanType[];
  discountDurationMonths?: number | null;
  // If omitted and offerType is STRIPE_DISCOUNT, the server mints a fresh
  // coupon with matching percent + duration. Populate to re-use an existing
  // coupon (useful when a marketing team has pre-negotiated Stripe IDs).
  stripeCouponId?: string | null;
}

export const listAdminCampaigns = async (): Promise<AdminCampaignRow[]> => {
  const { data } = await axios.get<AdminCampaignRow[]>("/admin/campaigns");
  return data;
};

export const fetchAdminCampaign = async (id: string): Promise<AdminCampaignDetail> => {
  const { data } = await axios.get<AdminCampaignDetail>(`/admin/campaigns/${id}`);
  return data;
};

export const createAdminCampaign = async (
  input: CreateCampaignInput,
): Promise<AdminCampaignDetail> => {
  const { data } = await axios.post<AdminCampaignDetail>("/admin/campaigns", input);
  return data;
};

export const updateAdminCampaign = async (
  id: string,
  input: Partial<CreateCampaignInput>,
): Promise<AdminCampaignDetail> => {
  const { data } = await axios.patch<AdminCampaignDetail>(`/admin/campaigns/${id}`, input);
  return data;
};

export const setCampaignEnabled = async (id: string, enabled: boolean) => {
  const { data } = await axios.post<AdminCampaignDetail>(
    `/admin/campaigns/${id}/${enabled ? "resume" : "pause"}`,
  );
  return data;
};

export const useAdminCampaigns = () =>
  useQuery({
    queryKey: ["admin", "campaigns"],
    queryFn: listAdminCampaigns,
    staleTime: 15_000,
  });

export const useAdminCampaign = (id: string | undefined) =>
  useQuery({
    queryKey: ["admin", "campaigns", id],
    queryFn: () => fetchAdminCampaign(id as string),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

export const useCreateAdminCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createAdminCampaign,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "campaigns"] });
      void qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    },
  });
};

export const useUpdateAdminCampaign = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateCampaignInput>) => updateAdminCampaign(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "campaigns"] });
      void qc.invalidateQueries({ queryKey: ["admin", "campaigns", id] });
    },
  });
};

export const useSetCampaignEnabled = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => setCampaignEnabled(id, enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "campaigns"] });
      void qc.invalidateQueries({ queryKey: ["admin", "campaigns", id] });
    },
  });
};
